import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../utils/api.js";
import { useAuth } from "../context/AuthContext";
import { resolveClientPostLoginPath } from "../utils/clientAppRedirect";
import { validatePassword } from "../utils/passwordValidation";
import roboImg from "../assets/spotbot-lite.png";
import logoImg from "../assets/spotless-logo-white.png";
import {
  FiEye,
  FiEyeOff,
  FiArrowLeft,
  FiMail,
  FiLock,
  FiShield,
  FiCpu,
  FiActivity,
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
} from "react-icons/fi";
import "./Login.css";

function AlertBanner({ message }) {
  if (!message) return null;
  const isError =
    /fail|invalid|wrong|error|blocked|try again/i.test(message) &&
    !/sent|verified|create/i.test(message);
  const isSuccess = /verified|sent|success/i.test(message);
  const variant = isSuccess ? "success" : isError ? "error" : "info";
  const Icon = isSuccess ? FiCheckCircle : isError ? FiAlertCircle : FiInfo;

  return (
    <div className={`login-alert login-alert--${variant}`} role="alert">
      <Icon className="login-alert__icon" aria-hidden />
      <p className="login-alert__text">{message}</p>
    </div>
  );
}

function EmailChip({ email, onChange }) {
  const initial = (email[0] || "?").toUpperCase();
  return (
    <div className="login-email-chip">
      <div className="login-email-chip__avatar" aria-hidden>
        {initial}
      </div>
      <div className="login-email-chip__info">
        <p className="login-email-chip__label">Signed in as</p>
        <p className="login-email-chip__email">{email}</p>
      </div>
      {onChange && (
        <button type="button" className="login-email-chip__change" onClick={onChange}>
          Change
        </button>
      )}
    </div>
  );
}

function FieldInput({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  icon: Icon,
  required = true,
  maxLength,
  autoComplete,
  showToggle,
  visible,
  onToggle,
}) {
  return (
    <div className="login-field">
      <label className="login-field__label">{label}</label>
      <div className="login-field__wrapper">
        {Icon && <Icon className="login-field__icon" aria-hidden />}
        <input
          className="login-field__input"
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          maxLength={maxLength}
          autoComplete={autoComplete}
        />
        {showToggle && (
          <button
            type="button"
            className="login-field__toggle"
            onClick={onToggle}
            aria-label={visible ? "Hide password" : "Show password"}
          >
            {visible ? <FiEyeOff /> : <FiEye />}
          </button>
        )}
      </div>
    </div>
  );
}

function OTPInput({ value, onChange, disabled }) {
  const refs = useRef([]);
  const digits = value.padEnd(6, " ").split("").slice(0, 6);

  const update = (next) => onChange(next.replace(/\s/g, "").slice(0, 6));

  const handleChange = (index, char) => {
    const cleaned = char.replace(/\D/g, "");
    if (!cleaned) return;
    const arr = digits.map((d) => (d === " " ? "" : d));
    arr[index] = cleaned[cleaned.length - 1];
    const next = arr.join("");
    update(next);
    if (index < 5) refs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = digits.map((d) => (d === " " ? "" : d));
      if (arr[index]) {
        arr[index] = "";
        update(arr.join(""));
      } else if (index > 0) {
        arr[index - 1] = "";
        update(arr.join(""));
        refs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      refs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) update(pasted);
    refs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div className="login-field">
      <label className="login-field__label">Verification code</label>
      <div className="login-otp" onPaste={handlePaste}>
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            className={[
              "login-otp__digit",
              digit.trim() && "login-otp__digit--filled",
            ]
              .filter(Boolean)
              .join(" ")}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit.trim()}
            disabled={disabled}
            aria-label={`Digit ${i + 1}`}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </div>
    </div>
  );
}

function PrimaryButton({ loading, disabled, children, ...props }) {
  return (
    <button
      type="submit"
      className="login-btn login-btn--primary"
      disabled={loading || disabled}
      {...props}
    >
      {loading ? (
        <>
          <span className="login-spinner" aria-hidden />
          Please wait…
        </>
      ) : (
        children
      )}
    </button>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOTP] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("login");
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const navigate = useNavigate();
  const { login: completeLogin } = useAuth();

  const startCooldown = useCallback(() => {
    setCooldownUntil(Date.now() + 45000);
    setCooldownSeconds(45);
  }, []);

  useEffect(() => {
    if (!cooldownUntil) return undefined;
    const tick = () => {
      const remaining = Math.ceil((cooldownUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setCooldownUntil(0);
        setCooldownSeconds(0);
      } else {
        setCooldownSeconds(remaining);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  useEffect(() => {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, []);

  const authBlocked = cooldownSeconds > 0;

  const resetToLogin = () => {
    setMode("login");
    setStep(1);
    setPassword("");
    setConfirmPassword("");
    setOTP("");
    setMessage("");
  };

  const goBackStep = () => {
    setMessage("");
    if (mode === "forget") {
      if (step === 4) setStep(3);
      else if (step === 3) setStep(1);
      else resetToLogin();
    } else if (mode === "firstTime") {
      if (step === 3) setStep(2);
      else if (step === 2) setStep(1);
      else resetToLogin();
    } else if (step === 2) {
      setStep(1);
      setPassword("");
    }
  };

  const getRedirectPath = (loginData) => {
    const clientPath = resolveClientPostLoginPath(loginData);
    if (clientPath) return clientPath;
    return loginData?.role === "user" ? "/client/overview" : "/content";
  };

  const handleValidateUser = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setMessage("");
    try {
      const response = await axios.post("/user/validate", { email });
      if (response.data.isFirstTime) {
        setMode("firstTime");
        setStep(2);
        setMessage("OTP sent to your email for verification");
      } else {
        setMode("login");
        setStep(2);
      }
    } catch (error) {
      startCooldown();
      setMessage(error.response?.data?.message || "Something went wrong during validation.");
    } finally {
      setLoading(false);
    }
  };

  const handleFirstTimeOTPVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      await axios.post("/user/verify-otp", { email, otp });
      setMessage("OTP verified! Please create your password.");
      setStep(3);
    } catch (error) {
      setMessage(error.response?.data?.message || "Invalid OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleFirstTimePasswordReset = async (e) => {
    e.preventDefault();
    const pwErrors = validatePassword(password, confirmPassword);
    if (pwErrors.length) {
      setMessage(pwErrors[0]);
      return;
    }
    if (authBlocked) return;
    setLoading(true);
    try {
      const response = await axios.post("/user/reset-password", { email, password });
      await completeLogin(response.data, email);
      navigate(getRedirectPath(response.data));
    } catch (error) {
      startCooldown();
      setMessage(error.response?.data?.message || "Failed to create password.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (authBlocked) return;
    setLoading(true);
    setMessage("");
    try {
      const response = await axios.post("/user/login", { email, password });
      await completeLogin(response.data, email);
      navigate(getRedirectPath(response.data));
    } catch (error) {
      startCooldown();
      setMessage(error.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgetPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      await axios.post("/user/forget-password", { email });
      setMessage("OTP sent to your email!");
      setStep(3);
    } catch (error) {
      setMessage(error.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      await axios.post("/user/password-verify-otp", { email, otp });
      setMessage("OTP verified! Please reset your password.");
      setStep(4);
    } catch (error) {
      setMessage(error.response?.data?.message || "Invalid OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    const pwErrors = validatePassword(password, confirmPassword);
    if (pwErrors.length) {
      setMessage(pwErrors[0]);
      return;
    }
    if (authBlocked) return;
    setLoading(true);
    try {
      const response = await axios.post("/user/password-reset", {
        email,
        password,
        confirmPassword,
      });
      await completeLogin(response.data, email);
      navigate(getRedirectPath(response.data));
    } catch (error) {
      startCooldown();
      setMessage(error.response?.data?.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (mode === "forget") return step === 4 ? "Reset password" : "Forgot password";
    if (mode === "firstTime") return "Account setup";
    return step === 2 ? "Welcome back" : "Sign in";
  };

  const getSubtitle = () => {
    if (mode === "forget") {
      if (step === 4) return "Choose a strong new password for your account.";
      if (step === 3) return "Enter the 6-digit code we sent to your email.";
      return "Enter your email and we'll send you a verification code.";
    }
    if (mode === "firstTime") {
      if (step === 3) return "Create a secure password to complete your setup.";
      if (step === 2) return "Enter the verification code sent to your email.";
      return "Enter your email to get started with Spotless AI.";
    }
    if (step === 2) return "Enter your password to access your dashboard.";
    return "Sign in to manage robots, teams, and operations.";
  };

  const getSubmitHandler = () => {
    if (mode === "forget") {
      if (step === 4) return handleResetPasswordSubmit;
      if (step === 3) return handleOTPVerify;
      return handleForgetPasswordSubmit;
    }
    if (mode === "firstTime") {
      if (step === 3) return handleFirstTimePasswordReset;
      if (step === 2) return handleFirstTimeOTPVerify;
      return handleValidateUser;
    }
    return step === 2 ? handleLogin : handleValidateUser;
  };

  const showBack =
    step > 1 || mode === "forget" || (mode === "firstTime" && step > 1);

  const renderPasswordFields = (newLabel = "New password", confirmLabel = "Confirm password") => (
    <>
      <FieldInput
        label={newLabel}
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="At least 8 characters"
        icon={FiLock}
        autoComplete="new-password"
        showToggle
        visible={showPassword}
        onToggle={() => setShowPassword(!showPassword)}
      />
      <FieldInput
        label={confirmLabel}
        type={showConfirmPassword ? "text" : "password"}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder="Re-enter your password"
        icon={FiLock}
        autoComplete="new-password"
        showToggle
        visible={showConfirmPassword}
        onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
      />
    </>
  );

  const renderFormBody = () => {
    if (mode === "forget") {
      if (step === 1 || step === 2) {
        return (
          <FieldInput
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            icon={FiMail}
            autoComplete="email"
          />
        );
      }
      if (step === 3) {
        return (
          <>
            <EmailChip email={email} />
            <OTPInput value={otp} onChange={setOTP} disabled={loading} />
          </>
        );
      }
      if (step === 4) return renderPasswordFields();
      return null;
    }

    if (mode === "firstTime") {
      if (step === 1) {
        return (
          <FieldInput
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            icon={FiMail}
            autoComplete="email"
          />
        );
      }
      if (step === 2) {
        return (
          <>
            <EmailChip email={email} onChange={() => { setStep(1); setOTP(""); setMessage(""); }} />
            <OTPInput value={otp} onChange={setOTP} disabled={loading} />
          </>
        );
      }
      if (step === 3) return renderPasswordFields("Create password", "Confirm password");
      return null;
    }

    if (step === 1) {
      return (
        <FieldInput
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          icon={FiMail}
          autoComplete="email"
        />
      );
    }

    return (
      <>
        <EmailChip
          email={email}
          onChange={() => {
            setStep(1);
            setPassword("");
            setMessage("");
          }}
        />
        <FieldInput
          label="Password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          icon={FiLock}
          autoComplete="current-password"
          showToggle
          visible={showPassword}
          onToggle={() => setShowPassword(!showPassword)}
        />
        <div className="login-actions__row">
          <span />
          <button
            type="button"
            className="login-btn--link"
            onClick={() => {
              setMode("forget");
              setStep(1);
              setPassword("");
              setMessage("");
            }}
          >
            Forgot password?
          </button>
        </div>
      </>
    );
  };

  const getSubmitLabel = () => {
    if (loading) return null;
    if (mode === "forget") {
      if (step === 4) return "Update password";
      if (step === 3) return "Verify code";
      return "Send verification code";
    }
    if (mode === "firstTime") {
      if (step === 3) return "Complete setup";
      if (step === 2) return "Verify account";
      return "Continue";
    }
    return step === 2 ? "Sign in" : "Continue";
  };

  const getSecondaryAction = () => {
    if (mode === "forget" && step === 3) {
      return (
        <button
          type="button"
          className="login-btn login-btn--ghost"
          onClick={() => {
            setStep(1);
            setOTP("");
            setMessage("");
          }}
        >
          Didn't receive code? Resend
        </button>
      );
    }
    if (mode === "firstTime" && step === 2) {
      return (
        <button
          type="button"
          className="login-btn login-btn--ghost"
          onClick={() => {
            setStep(1);
            setOTP("");
            setMessage("");
          }}
        >
          Resend verification code
        </button>
      );
    }
    if (mode === "forget" && step <= 2) {
      return (
        <button type="button" className="login-btn login-btn--ghost" onClick={resetToLogin}>
          <FiArrowLeft aria-hidden /> Back to sign in
        </button>
      );
    }
    return null;
  };

  return (
    <div className="login-page">
      <div className="login-bg" aria-hidden>
        <div className="login-bg__gradient" />
        <div className="login-bg__grid" />
        <div className="login-bg__orb login-bg__orb--1" />
        <div className="login-bg__orb login-bg__orb--2" />
        <div className="login-bg__orb login-bg__orb--3" />
      </div>

      <img src={logoImg} alt="Spotless AI" className="login-page__logo" />

      <div className="login-page__badge">
        <span className="login-page__badge-dot" />
        Admin Portal
      </div>

      <div className="login-shell">
        <aside className="login-brand">
          <div className="login-brand__content">
            <div className="login-brand__text">
              <h1 className="login-brand__title">
                Intelligent automation,{" "}
                <span className="login-brand__title-accent">spotless control</span>
              </h1>
              <p className="login-brand__desc">
                Manage your robot fleet, monitor operations, and keep your team
                running smoothly — all from one secure dashboard.
              </p>
              <ul className="login-brand__features">
                <li className="login-brand__feature">
                  <span className="login-brand__feature-icon">
                    <FiCpu />
                  </span>
                  Real-time robot fleet monitoring
                </li>
                <li className="login-brand__feature">
                  <span className="login-brand__feature-icon">
                    <FiShield />
                  </span>
                  Enterprise-grade security &amp; access control
                </li>
                <li className="login-brand__feature">
                  <span className="login-brand__feature-icon">
                    <FiActivity />
                  </span>
                  Live analytics and operational insights
                </li>
              </ul>
            </div>

            <div className="login-brand__visual">
              <div className="login-brand__glow" />
              <img src={roboImg} alt="Spotless robot" className="login-brand__robot" />
            </div>
          </div>
        </aside>

        <main className="login-panel">
          <div className="login-card">
            <header className="login-card__header">
              {showBack && (
                <button type="button" className="login-card__back" onClick={goBackStep}>
                  <FiArrowLeft aria-hidden /> Back
                </button>
              )}
              <h2 className="login-card__title">{getTitle()}</h2>
              <p className="login-card__subtitle">{getSubtitle()}</p>
            </header>

            <AlertBanner message={message} />

            {authBlocked && (
              <div className="login-cooldown" role="status">
                Too many attempts. Try again in{" "}
                <span className="login-cooldown__timer">{cooldownSeconds}s</span>
              </div>
            )}

            <form className="login-form" onSubmit={getSubmitHandler()}>
              {renderFormBody()}

              <div className="login-actions">
                <PrimaryButton loading={loading} disabled={authBlocked}>
                  {getSubmitLabel()}
                </PrimaryButton>
                {getSecondaryAction()}
              </div>
            </form>

            <p className="login-panel__footer">
              Protected by Spotless AI secure authentication
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
